import type { LiveEvent, TikTokUser, BattleTeam, BattleTeamUser, BattleHost, BattleContributor } from './types.js';

export function decodeVarint(buf: Buffer, offset: number): { value: number; offset: number } {
    let result = 0, shift = 0;
    while (offset < buf.length) {
        const byte = buf[offset++];
        result |= (byte & 0x7F) << shift;
        if ((byte & 0x80) === 0) break;
        shift += 7;
    }
    return { value: result >>> 0, offset };
}

export function decodeVarint64(buf: Buffer, offset: number): { value: bigint; offset: number } {
    let result = 0n, shift = 0n;
    while (offset < buf.length) {
        const byte = BigInt(buf[offset++]);
        result |= (byte & 0x7Fn) << shift;
        if ((byte & 0x80n) === 0n) break;
        shift += 7n;
    }
    return { value: result, offset };
}

export function encodeVarint(v: number | bigint): Buffer {
    const bytes: number[] = [];
    let n = typeof v === 'bigint' ? v : BigInt(v);
    do {
        let b = Number(n & 0x7Fn);
        n >>= 7n;
        if (n > 0n) b |= 0x80;
        bytes.push(b);
    } while (n > 0n);
    return Buffer.from(bytes);
}

export interface ProtoField {
    fn: number;
    wt: number;
    value: Buffer | number | bigint;
}

export function encodeField(fn: number, wt: number, value: Buffer | bigint | number | string): Buffer {
    const tag = encodeVarint((fn << 3) | wt);
    if (wt === 0) {
        return Buffer.concat([tag, encodeVarint(typeof value === 'number' ? BigInt(value) : value as bigint)]);
    }
    const data = typeof value === 'string' ? Buffer.from(value) : value as Buffer;
    return Buffer.concat([tag, encodeVarint(data.length), data]);
}

export function decodeProto(buf: Buffer): ProtoField[] {
    const fields: ProtoField[] = [];
    let offset = 0;
    while (offset < buf.length) {
        const tagResult = decodeVarint(buf, offset);
        offset = tagResult.offset;
        const fn = tagResult.value >> 3;
        const wt = tagResult.value & 7;
        if (wt === 0) {
            const r = decodeVarint64(buf, offset);
            offset = r.offset;
            fields.push({ fn, wt, value: r.value });
        } else if (wt === 2) {
            const lenR = decodeVarint(buf, offset);
            offset = lenR.offset;
            const data = buf.subarray(offset, offset + lenR.value);
            offset += lenR.value;
            fields.push({ fn, wt, value: data });
        } else if (wt === 1) {
            fields.push({ fn, wt, value: buf.readBigInt64LE(offset) });
            offset += 8;
        } else if (wt === 5) {
            fields.push({ fn, wt, value: BigInt(buf.readInt32LE(offset)) });
            offset += 4;
        } else {
            break;
        }
    }
    return fields;
}

export function getStr(fields: ProtoField[], fn: number): string {
    const f = fields.find(x => x.fn === fn && x.wt === 2);
    return f ? (f.value as Buffer).toString('utf-8') : '';
}

export function getBytes(fields: ProtoField[], fn: number): Buffer | null {
    const f = fields.find(x => x.fn === fn && x.wt === 2);
    return f ? f.value as Buffer : null;
}

export function getInt(fields: ProtoField[], fn: number): number {
    const f = fields.find(x => x.fn === fn && x.wt === 0);
    return f ? Number(f.value) : 0;
}

/** Like getInt but returns BigInt as string to preserve full 64-bit precision for user IDs */
export function getIntStr(fields: ProtoField[], fn: number): string {
    const f = fields.find(x => x.fn === fn && x.wt === 0);
    return f ? String(f.value) : '0';
}

export function getAllBytes(fields: ProtoField[], fn: number): Buffer[] {
    return fields.filter(x => x.fn === fn && x.wt === 2).map(x => x.value as Buffer);
}

export function buildHeartbeat(roomId: string): Buffer {
    const payload = encodeField(1, 0, BigInt(roomId));
    return Buffer.concat([
        encodeField(6, 2, 'pb'),
        encodeField(7, 2, 'hb'),
        encodeField(8, 2, payload),
    ]);
}

export function buildImEnterRoom(roomId: string): Buffer {
    const inner = Buffer.concat([
        encodeField(1, 0, BigInt(roomId)),
        encodeField(4, 0, 12n),
        encodeField(5, 2, 'audience'),
        encodeField(6, 2, ''),
        encodeField(9, 2, ''),
        encodeField(10, 2, ''),
    ]);
    return Buffer.concat([
        encodeField(6, 2, 'pb'),
        encodeField(7, 2, 'im_enter_room'),
        encodeField(8, 2, inner),
    ]);
}

export function buildAck(id: bigint): Buffer {
    return Buffer.concat([
        encodeField(2, 0, id),
        encodeField(6, 2, 'pb'),
        encodeField(7, 2, 'ack'),
    ]);
}

// ── User parsing ────────────────────────────────────────────────────
// Proto: User { userId=1, nickname=3, profilePicture=9, extraAttributes=22, badge=64, uniqueId=38 }
// Proto: LinkUser { userId=1, nickname=2, profilePicture=3, uniqueId=4 }
// Proto: ProfilePicture { repeated urls=1 }
// NOTE: Army users in WebcastLinkMicArmies use a DIFFERENT layout where
//       field 38 contains avatar/image data, NOT uniqueId.
//       We detect this by checking if the string looks like a username (short, no URLs).

function looksLikeUsername(s: string): boolean {
    // Valid TikTok usernames are short (max ~30 chars), no URLs, no binary
    return s.length > 0 && s.length <= 50 && !s.includes('://') && !s.includes('\x00');
}

function parseUser(data: Buffer): TikTokUser {
    const f = decodeProto(data);
    // Use getIntStr for user ID to preserve full 64-bit precision
    const id = getIntStr(f, 1) !== '0' ? getIntStr(f, 1) : getStr(f, 1);
    const nickname = getStr(f, 3) || getStr(f, 2);

    // uniqueId: Try field 4 (LinkUser format) first, then field 38 (User format)
    // Validate that the result looks like a username, not binary/URL data
    let uniqueId = '';
    const uid4 = getStr(f, 4);
    const uid38 = getStr(f, 38);
    if (uid4 && looksLikeUsername(uid4)) {
        uniqueId = uid4;
    } else if (uid38 && looksLikeUsername(uid38)) {
        uniqueId = uid38;
    } else {
        // Fall back to field 2 (nickname field in LinkUser)
        const uid2 = getStr(f, 2);
        if (uid2 && looksLikeUsername(uid2)) uniqueId = uid2;
    }

    // profilePicture: Try field 9 (User format), field 4 (army users — avatar at field 4), field 3 (LinkUser format)
    // Can't use OR chain because field 3 = nickname bytes in User format (truthy but wrong)
    // Must try each and pick the first that yields a valid URL
    let profilePicture: string | undefined;
    for (const fieldNum of [9, 4, 3]) {
        if (profilePicture) break;
        const avatarBuf = getBytes(f, fieldNum);
        if (!avatarBuf) continue;
        try {
            const avatarFields = decodeProto(avatarBuf);
            const urlBufs = getAllBytes(avatarFields, 1);
            for (const urlBuf of urlBufs) {
                const url = urlBuf.toString('utf-8');
                if (url.includes('://')) {
                    profilePicture = url;
                    break;
                }
            }
        } catch { }
    }

    // avatarLarge: Try field 11 (high-resolution ~720x720 avatar)
    // This is separate from profilePicture which uses the small thumbnail
    let avatarLargeUrl: string | undefined;
    const largeBuf = getBytes(f, 11);
    if (largeBuf) {
        try {
            const largeFields = decodeProto(largeBuf);
            const urlBufs = getAllBytes(largeFields, 1);
            for (const urlBuf of urlBufs) {
                const url = urlBuf.toString('utf-8');
                if (url.includes('://')) {
                    avatarLargeUrl = url;
                    break;
                }
            }
        } catch { }
    }
    // Also try field 10 (avatarMedium) as fallback for avatarLargeUrl
    if (!avatarLargeUrl) {
        const medBuf = getBytes(f, 10);
        if (medBuf) {
            try {
                const medFields = decodeProto(medBuf);
                const urlBufs = getAllBytes(medFields, 1);
                for (const urlBuf of urlBufs) {
                    const url = urlBuf.toString('utf-8');
                    if (url.includes('://')) {
                        avatarLargeUrl = url;
                        break;
                    }
                }
            } catch { }
        }
    }

    // Badges from field 64 → field 21 (repeated UserBadge)
    const badges: string[] = [];
    const badgeBuf = getBytes(f, 64);
    if (badgeBuf) {
        try {
            const badgeFields = decodeProto(badgeBuf);
            const badgeItems = getAllBytes(badgeFields, 21);
            for (const bi of badgeItems) {
                const bf = decodeProto(bi);
                const name = getStr(bf, 3) || getStr(bf, 2);
                if (name) badges.push(name);
            }
        } catch { }
    }

    return {
        id,
        nickname,
        uniqueId: uniqueId || nickname || id,
        profilePicture,
        avatarLargeUrl,
        badges: badges.length > 0 ? badges : undefined,
    };
}

// ── Battle parsing ──────────────────────────────────────────────────
// Proto: WebcastLinkMicBattle { repeated battleUsers=10 }
//   battleUsers → WebcastLinkMicBattleItems { battleGroup=2 }
//     battleGroup → WebcastLinkMicBattleGroup { LinkUser user=1 }
//
// Proto: WebcastLinkMicArmies { repeated battleItems=3, battleStatus=7 }
//   battleItems → WebcastLinkMicArmiesItems { hostUserId=1, repeated battleGroups=2 }
//     battleGroups → WebcastLinkMicArmiesGroup { repeated users=1, points=2 }

function parseBattleTeamFromArmies(itemBuf: Buffer): BattleTeam {
    const f = decodeProto(itemBuf);
    const hostUserId = getIntStr(f, 1);
    let teamScore = 0;
    const users: BattleTeamUser[] = [];

    // battleGroups at field 2
    const groups = getAllBytes(f, 2);
    for (const gb of groups) {
        try {
            const gf = decodeProto(gb);
            const points = getInt(gf, 2);
            teamScore += points;

            // users at field 1 (repeated)
            const userBufs = getAllBytes(gf, 1);

            // TikTok proto structure: WebcastLinkMicArmiesGroup { users=1, points=2 }
            // - When 1 user per group: points IS that user's individual contribution
            // - When N users per group: points is the GROUP TOTAL, NOT individual scores
            //   Individual scores are NOT available in WebcastLinkMicArmies proto.
            // Use -1 to signal "individual score unavailable" to the UI.
            const individualScore = userBufs.length === 1 ? points : -1;

            for (const ub of userBufs) {
                try {
                    const user = parseUser(ub);
                    users.push({ user, score: individualScore });
                } catch { }
            }
        } catch { }
    }

    return { hostUserId, score: teamScore, users };
}

// ── Message-specific parsers ────────────────────────────────────────

export function parseWebcastMessage(method: string, payload: Buffer): LiveEvent {
    const f = decodeProto(payload);
    const base = { timestamp: Date.now(), msgId: '' };

    // Try to extract msgId from MessageType (field 1 submessage, field 4 = timestamp)
    const typeBuf = getBytes(f, 1);
    if (typeBuf) {
        try {
            const tf = decodeProto(typeBuf);
            const ts = getInt(tf, 4);
            if (ts) base.timestamp = ts;
        } catch { }
    }

    switch (method) {
        // Proto: WebcastChatMessage { MessageType type=1, User user=2, string comment=3, ..., repeated WebcastSubEmote emotes=12 }
        // WebcastSubEmote { int32 placeInComment=1, EmoteDetails emote=2 }
        // EmoteDetails { string emoteId=1, EmoteImage image=2 }
        // EmoteImage { string imageUrl=1 }
        case 'WebcastChatMessage': {
            const userBuf = getBytes(f, 2);
            const user = userBuf ? parseUser(userBuf) : { id: '0', nickname: '', uniqueId: '' };
            const comment = getStr(f, 3);

            // Parse inline emotes (try field 12 and 13 — field number varies across TikTok versions)
            const emotes: Array<{ emoteId: string; imageUrl: string; placeInComment: number }> = [];
            const emoteBufs12 = getAllBytes(f, 12);
            const emoteBufs13 = getAllBytes(f, 13);
            const emoteBufs = emoteBufs13.length > 0 ? emoteBufs13 : emoteBufs12;
            for (const eb of emoteBufs) {
                try {
                    const ef = decodeProto(eb);
                    const placeInComment = getInt(ef, 1);
                    const detailBuf = getBytes(ef, 2);
                    if (detailBuf) {
                        const df = decodeProto(detailBuf);
                        const emoteId = getStr(df, 1);
                        let imageUrl = '';
                        const imgBuf = getBytes(df, 2);
                        if (imgBuf) {
                            const imgf = decodeProto(imgBuf);
                            imageUrl = getStr(imgf, 1);
                        }
                        if (emoteId) {
                            emotes.push({ emoteId, imageUrl, placeInComment });
                        }
                    }
                } catch { }
            }

            return { ...base, type: 'chat' as const, user, comment, emotes };
        }

        // Proto: WebcastMemberMessage { User user=2, WebcastMessageEvent event=1 }
        case 'WebcastMemberMessage': {
            const userBuf = getBytes(f, 2);
            const user = userBuf ? parseUser(userBuf) : { id: '0', nickname: '', uniqueId: '' };
            let action = 1;
            const eventBuf = getBytes(f, 1);
            if (eventBuf) {
                const ef = decodeProto(eventBuf);
                const detailBuf = getBytes(ef, 8);
                if (detailBuf) {
                    const df = decodeProto(detailBuf);
                    const label = getStr(df, 2);
                    if (label.includes('followed')) action = 2;
                    else if (label.includes('share')) action = 3;
                }
            }
            return { ...base, type: 'member' as const, user, action };
        }

        // Proto: WebcastLikeMessage { User user=5, WebcastMessageEvent event=1, int32 likeCount=2, int32 totalLikeCount=3 }
        case 'WebcastLikeMessage': {
            const userBuf = getBytes(f, 5);
            const user = userBuf ? parseUser(userBuf) : { id: '0', nickname: '', uniqueId: '' };
            return {
                ...base, type: 'like' as const, user,
                likeCount: getInt(f, 2),
                totalLikes: getInt(f, 3),
            };
        }

        // Proto: WebcastGiftMessage {
        //   User user=7, int32 giftId=2, int32 repeatCount=5, int32 repeatEnd=9,
        //   GiftDetails giftDetails=15, GiftExtra giftExtra=23
        // }
        // GiftDetails { GiftImage giftImage=1, string giftName=16, string describe=2,
        //               int32 giftType=11, int32 diamondCount=12 }
        // GiftExtra { uint64 timestamp=6, uint64 toUserId=8 }
        case 'WebcastGiftMessage': {
            const userBuf = getBytes(f, 7);
            const user = userBuf ? parseUser(userBuf) : { id: '0', nickname: '', uniqueId: '' };
            const giftId = getInt(f, 2);
            const repeatCount = getInt(f, 5);
            const repeatEnd = getInt(f, 9) === 1;

            let giftName = '', diamondCount = 0, giftType = 0;
            let giftImageUrl = '';
            const detailsBuf = getBytes(f, 15);
            if (detailsBuf) {
                const df = decodeProto(detailsBuf);
                giftName = getStr(df, 16) || getStr(df, 2);
                diamondCount = getInt(df, 12);
                giftType = getInt(df, 11);

                const imgBuf = getBytes(df, 1);
                if (imgBuf) {
                    const imgf = decodeProto(imgBuf);
                    giftImageUrl = getStr(imgf, 1);
                }
            }

            let toUserId = '';
            const extraBuf = getBytes(f, 23);
            if (extraBuf) {
                const ef = decodeProto(extraBuf);
                toUserId = String(getInt(ef, 8));
            }

            const groupId = toUserId || getStr(f, 11);

            return {
                ...base, type: 'gift' as const, user, giftId, giftName, diamondCount,
                repeatCount, repeatEnd, combo: repeatCount > 1 && !repeatEnd,
                giftType, groupId,
            };
        }

        // Proto: WebcastSocialMessage { User user=2, WebcastMessageEvent event=1 }
        case 'WebcastSocialMessage': {
            const userBuf = getBytes(f, 2);
            const user = userBuf ? parseUser(userBuf) : { id: '0', nickname: '', uniqueId: '' };
            let action = 'follow';
            const eventBuf = getBytes(f, 1);
            if (eventBuf) {
                const ef = decodeProto(eventBuf);
                const detailBuf = getBytes(ef, 8);
                if (detailBuf) {
                    const df = decodeProto(detailBuf);
                    const label = getStr(df, 2);
                    if (label.includes('share')) action = 'share';
                    else if (label.includes('follow')) action = 'follow';
                    const displayType = getStr(df, 1);
                    if (displayType === 'pm_mt_msg_viewer_share') action = 'share';
                }
            }
            return { ...base, type: 'social' as const, user, action };
        }

        // Proto: WebcastRoomUserSeqMessage { int32 viewerCount=3 }
        case 'WebcastRoomUserSeqMessage': {
            const viewerCount = getInt(f, 3) || getInt(f, 2);
            const totalViewers = getInt(f, 1) || viewerCount;
            return { ...base, type: 'roomUserSeq' as const, totalViewers, viewerCount };
        }

        // Proto: WebcastLinkMicBattle { repeated WebcastLinkMicBattleItems battleUsers=10 }
        //   battleUsers → { battleGroup=2 → { LinkUser user=1 } }
        case 'WebcastLinkMicBattle': {
            // 🔧 Field map (corrected 2026-05-20):
            //   field 1 = roomId (BigInt — was mislabeled as battleId)
            //   field 2 = roomId again (BigInt — was mislabeled as status; caused
            //             customer to see `status: 7641983148117036000`)
            //   field 3 = battleDuration
            //   field 4 = battleId (the actual PK match ID)
            //   field 7 = battle status (1=ACTIVE, 2=STARTING, 3=ENDED, 4=PREPARING)
            //   field 10 = battleUsers (host-only, no scores)
            //   field 14 = hosts with teamTotalScore + contributors
            const battleId = String(getInt(f, 4) || getInt(f, 1) || '');
            let status = getInt(f, 7);
            if (!status) {
                const fallback = getInt(f, 2);
                status = (fallback > 0 && fallback < 100) ? fallback : 1;
            }
            const battleDuration = getInt(f, 3);
            const teams: BattleTeam[] = [];

            // Field 14 — hosts with scores (preferred).
            const hostBufs = getAllBytes(f, 14);
            for (const hb of hostBufs) {
                try {
                    const hf = decodeProto(hb);
                    const hostUserId = String(getInt(hf, 1) || '');
                    const teamTotalScore = getInt(hf, 3);
                    const users: BattleTeamUser[] = [];
                    const containerBuf = getBytes(hf, 4);
                    if (containerBuf) {
                        const cf = decodeProto(containerBuf);
                        const userBufs = getAllBytes(cf, 1);
                        for (const ub of userBufs) {
                            try {
                                const uf = decodeProto(ub);
                                users.push({
                                    user: {
                                        id: String(getInt(uf, 1) || ''),
                                        nickname: getStr(uf, 3) || '',
                                        uniqueId: getStr(uf, 3) || '',
                                    } as any,
                                    score: getInt(uf, 2),
                                });
                            } catch { }
                        }
                    }
                    teams.push({ hostUserId, score: teamTotalScore, users });
                } catch { }
            }

            // Fallback: field 7 (battleArmies-style with scores)
            if (teams.length === 0) {
                const teamBufs7 = getAllBytes(f, 7);
                for (const tb of teamBufs7) {
                    try {
                        teams.push(parseBattleTeamFromArmies(tb));
                    } catch { }
                }
            }

            // Last resort: field 10 (host-only, no scores)
            if (teams.length === 0) {
                const battleUserBufs = getAllBytes(f, 10);
                for (const bub of battleUserBufs) {
                    try {
                        const bf = decodeProto(bub);
                        const groupBuf = getBytes(bf, 2);
                        if (groupBuf) {
                            const gf = decodeProto(groupBuf);
                            const linkUserBuf = getBytes(gf, 1);
                            if (linkUserBuf) {
                                const user = parseUser(linkUserBuf);
                                teams.push({
                                    hostUserId: user.id,
                                    score: 0,
                                    users: [{ user, score: 0 }],
                                });
                            }
                        }
                    } catch { }
                }
            }

            return { ...base, type: 'battle' as const, battleId, status, battleDuration, teams };
        }

        // Proto: WebcastLinkMicArmies { msg_header=1, matchId=2, session_id=4, startedAtMs=5,
        //   serverTsMs=6, repeated battleItems=3, status=7, sessionTag=17,
        //   battle_config=18 { session_id=1, start_time_ms=2, duration_s=3 },
        //   repeated hosts=14 { hostUserId=1, teamTotalScore=3, teamIdx=5,
        //     userContainer=4 { repeated users=1 { userId=1, score=2, nickname=3 } } }
        case 'WebcastLinkMicArmies': {
            // field 17 = sessionTag (hex string ~34 chars)
            // field 2  = matchId (stable across multi-round PK)
            // field 1  = message header (NOT the battle ID)
            const sessionTag = getStr(f, 17);
            // battleId historically = getInt(f, 2) for backwards compat. Same field also = matchId.
            const matchId = getIntStr(f, 2);
            const sessionId = getIntStr(f, 4);
            const battleId = sessionTag || matchId;
            const battleStatus = getInt(f, 7);
            const serverTsMs = getInt(f, 6);
            const teams: BattleTeam[] = [];

            const itemBufs = getAllBytes(f, 3);
            for (const ib of itemBufs) {
                try {
                    teams.push(parseBattleTeamFromArmies(ib));
                } catch { }
            }

            // Parse battle timer from field 18 (battle config)
            let battleStartMs = 0;
            let battleDurationSeconds = 0;
            const configBuf = getBytes(f, 18);
            if (configBuf) {
                try {
                    const cf = decodeProto(configBuf);
                    battleStartMs = getInt(cf, 2);    // start time in ms
                    battleDurationSeconds = getInt(cf, 3); // duration in seconds
                } catch { }
            }

            // secsRemaining computed using TikTok server clock (no VPS drift) when available.
            let secsRemaining = 0;
            if (battleStartMs > 0 && battleDurationSeconds > 0 && serverTsMs > 0) {
                secsRemaining = Math.max(0, battleDurationSeconds - Math.floor((serverTsMs - battleStartMs) / 1000));
            }

            // Compute endTimeMs for consumers to compute time left with their own clock
            // IMPORTANT: Do NOT use Date.now() here — VPS clock may differ from TikTok server
            // by 10-30 seconds, causing timer drift. Let the consumer compute timeLeft.
            let endTimeMs = 0;
            let timeLeftSeconds = 0;
            if (battleStartMs > 0 && battleDurationSeconds > 0) {
                endTimeMs = battleStartMs + battleDurationSeconds * 1000;
                // Best-effort estimate using VPS clock (may be off by up to 30s)
                const elapsedMs = Date.now() - battleStartMs;
                timeLeftSeconds = Math.max(0, battleDurationSeconds - Math.floor(elapsedMs / 1000));
            }

            // Multi-guest host blocks (field_14 repeated). Each entry = one PK-side host with
            // per-gifter MVP breakdown sorted high → low.
            const hosts: BattleHost[] = [];
            const hostBufs = getAllBytes(f, 14);
            for (const hb of hostBufs) {
                try {
                    const hf = decodeProto(hb);
                    const hostUserId = getIntStr(hf, 1);
                    const teamTotalScore = getInt(hf, 3);
                    const teamIdx = getInt(hf, 5);
                    const contributors: BattleContributor[] = [];
                    const containerBuf = getBytes(hf, 4);
                    if (containerBuf) {
                        const cf = decodeProto(containerBuf);
                        const userBufs = getAllBytes(cf, 1);
                        for (const ub of userBufs) {
                            try {
                                const uf = decodeProto(ub);
                                contributors.push({
                                    userId: getIntStr(uf, 1),
                                    score: getInt(uf, 2),
                                    nickname: getStr(uf, 3) || '',
                                });
                            } catch { }
                        }
                        contributors.sort((a, b) => b.score - a.score); // MVP first
                    }
                    hosts.push({ hostUserId, teamTotalScore, teamIdx, contributors });
                } catch { }
            }

            return {
                ...base, type: 'battleArmies' as const, battleId, teams,
                status: battleStatus, battleStartMs, battleDurationSeconds, timeLeftSeconds, endTimeMs,
                matchId, sessionId, serverTsMs, sessionTag,
                durationSec: battleDurationSeconds, secsRemaining,
                hosts,
            };
        }

        // Proto: WebcastLinkMicBattleItemCard
        //   #2 battleId
        //   #3 cardType (2=gloves/crit, 3=mist, 4=match_guide, 10=x2?, 11=x3, ...)
        //   #5/#6/#7/#14 inner (location varies with cardType — scan numerically):
        //     #1.{
        //       #1 effectKey
        //       #2 icon block { #1 repeated CDN urls, #2 iconKey, #5 accentColor }
        //       #3 activatedAtSec (unix seconds)
        //       #4 sender block: #1 sub { #1 userId, #2 nickname, #3 avatar block, #4 uniqueId }
        //       #5 durationSec
        //       #8 senderUserId (new boosters)
        //       #11 endsAtSec
        //       #12 senderUserId (old gloves/mist)
        //     }
        //     #3 comment template msg → #2 commentText
        case 'WebcastLinkMicBattleItemCard': {
            const battleId = getIntStr(f, 2);
            const cardType = getInt(f, 3);

            // Inner block field-number varies with cardType. Verified via live capture:
            //   cardType=2 (gloves/crit)  → field_5
            //   cardType=3 (mist)         → field_6
            //   cardType=4 (match_guide)  → field_7
            //   cardType=11 (x3 booster)  → field_14
            // Scan numerically — first present sub-message wins.
            let inner: ProtoField[] | null = null;
            for (const fn of [5, 6, 7, 14, 15, 16, 8, 9, 10, 11, 12, 13]) {
                const buf = getBytes(f, fn);
                if (buf) { inner = decodeProto(buf); break; }
            }

            let effectKey = '';
            let effect = 'unknown';
            let multiplier = 0;
            let activatedAtSec = 0;
            let durationSec = 0;
            let endsAtSec = 0;
            let senderUserId = '';
            let senderNickname = '';
            let senderUniqueId = '';
            let senderAvatarUrl = '';
            let commentTemplate = '';
            let iconUrl = '';
            let iconKey = '';
            let accentColor = '';

            if (inner) {
                // 🔥 Name block lives at DIFFERENT inner-field depending on cardType.
                // Verified live via battle:proto:capture (2026-05-20):
                //   cardType 2/3/4 (gloves/mist/match_guide)  → nameBlock = inner.field_1
                //   cardType 10/11  (x2/x3 boosters)          → nameBlock = inner.field_3
                // Try field_1 first; if its decoded effectKey is empty, fall back to field_3.
                let nameBuf = getBytes(inner, 1);
                let nf = nameBuf ? decodeProto(nameBuf) : [];
                let probedKey = nf.length ? (getStr(nf, 1) || '') : '';
                if (!probedKey) {
                    const altBuf = getBytes(inner, 3);
                    if (altBuf) {
                        const altFields = decodeProto(altBuf);
                        const altKey = getStr(altFields, 1) || '';
                        if (altKey) {
                            nameBuf = altBuf;
                            nf = altFields;
                            probedKey = altKey;
                        }
                    }
                }
                if (nameBuf && nf.length) {
                    effectKey = probedKey;
                    // Map known effect keys:
                    //   pm_mt_boost_<X>_name / pm_mt_boost_send_<X>_comment → gloves/mist
                    //   ttlive_match_announcement_userSentBooster_noN       → x2/x3 boosters
                    //   ttlive_match_gotPowerUp_panel_noN_title             → x2/x3 boosters
                    //   pm_mt_match_guide_gotPowerUps                       → broadcast panel
                    // TikTok's protobuf key is `pm_mt_boost_crit_name` but the
                    // in-app booster TikTok shows is the HAMMER (critical strike).
                    // We were emitting 'gloves' here, which is a different older
                    // booster that no longer exists in the live UI.
                    if (effectKey === 'pm_mt_boost_crit_name') effect = 'hammer';
                    else if (effectKey === 'pm_mt_boost_glove_name' || effectKey === 'pm_mt_boost_gloves_name') effect = 'gloves';
                    else if (effectKey === 'pm_mt_boost_mist_name') effect = 'mist';
                    else if (effectKey === 'pm_mt_match_guide_gotPowerUps') effect = 'match_guide';
                    else if (effectKey.includes('userSentBooster_no2') || effectKey.includes('gotPowerUp_panel_no2')) { effect = 'booster_x2'; multiplier = 2; }
                    else if (effectKey.includes('userSentBooster_no3') || effectKey.includes('gotPowerUp_panel_no3')) { effect = 'booster_x3'; multiplier = 3; }
                    else if (effectKey.includes('thunder')) effect = 'thunder';
                    else if (effectKey.includes('extra_time') || effectKey.includes('extra_round')) effect = 'extra_time';
                    else if (effectKey) effect = effectKey;

                    activatedAtSec = getInt(nf, 3);
                    durationSec = getInt(nf, 5);
                    endsAtSec = getInt(nf, 11) || getInt(nf, 7);
                    // senderUserId at field_12 (old gloves/mist) OR field_8 (new boosters).
                    senderUserId = getIntStr(nf, 12);
                    if (senderUserId === '0') senderUserId = getIntStr(nf, 8);
                    if (senderUserId === '0') senderUserId = getStr(nf, 13) || getStr(nf, 9) || '';

                    // Icon block at nf.field_2 — first field_1 URL + field_2 short key + field_5 accent hex.
                    const iconBuf = getBytes(nf, 2);
                    if (iconBuf) {
                        const icf = decodeProto(iconBuf);
                        const urlBufs = getAllBytes(icf, 1);
                        for (const ub of urlBufs) {
                            const candidate = ub.toString('utf-8');
                            if (candidate.includes('://')) { iconUrl = candidate; break; }
                        }
                        iconKey = getStr(icf, 2) || '';
                        accentColor = getStr(icf, 5) || '';
                    }

                    // Sender block: nf.field_4.field_1 = { userId, nickname, avatarMsg, uniqueId }
                    const senderOuter = getBytes(nf, 4);
                    if (senderOuter) {
                        const sf = decodeProto(senderOuter);
                        const sub = getBytes(sf, 1);
                        if (sub) {
                            const su = decodeProto(sub);
                            const nickBuf = getBytes(su, 2);
                            if (nickBuf) {
                                try { senderNickname = nickBuf.toString('utf-8'); } catch { }
                            }
                            senderUniqueId = getStr(su, 4) || '';
                            if (!senderUserId || senderUserId === '0') senderUserId = getIntStr(su, 1);
                            // Avatar: su.field_3 wraps a list of CDN URLs (field_1 repeated)
                            const avBuf = getBytes(su, 3);
                            if (avBuf) {
                                const af = decodeProto(avBuf);
                                const urlBufs = getAllBytes(af, 1);
                                for (const ub of urlBufs) {
                                    const candidate = ub.toString('utf-8');
                                    if (candidate.includes('://')) { senderAvatarUrl = candidate; break; }
                                }
                            }
                        }
                    }
                }

                // Comment template (e.g. "{0:user} sent 1 magic mist")
                const commentBuf = getBytes(inner, 3);
                if (commentBuf) {
                    const cf = decodeProto(commentBuf);
                    commentTemplate = getStr(cf, 2) || '';
                }
            }

            return {
                ...base, type: 'battleItemCard' as const, battleId, cardType,
                effect, effectKey, multiplier,
                senderUserId, senderNickname, senderUniqueId, senderAvatarUrl,
                activatedAtSec, durationSec, endsAtSec, commentTemplate,
                iconUrl, iconKey, accentColor,
            };
        }

        // Proto: WebcastSubNotifyMessage { User user=2, ... subMonth=3 }
        case 'WebcastSubNotifyMessage': {
            const userBuf = getBytes(f, 2);
            const user = userBuf ? parseUser(userBuf) : { id: '0', nickname: '', uniqueId: '' };
            return { ...base, type: 'subscribe' as const, user, subMonth: getInt(f, 3) };
        }

        case 'WebcastEmoteChatMessage': {
            const userBuf = getBytes(f, 2);
            const user = userBuf ? parseUser(userBuf) : { id: '0', nickname: '', uniqueId: '' };
            let emoteId = '', emoteUrl = '';
            const emoteBuf = getBytes(f, 3);
            if (emoteBuf) {
                const ef = decodeProto(emoteBuf);
                emoteId = getStr(ef, 1);
                const imageBuf = getBytes(ef, 2);
                if (imageBuf) {
                    const imgFields = decodeProto(imageBuf);
                    emoteUrl = getStr(imgFields, 1);
                }
            }
            return { ...base, type: 'emoteChat' as const, user, emoteId, emoteUrl };
        }

        case 'WebcastEnvelopeMessage': {
            const envelopeId = String(getInt(f, 1) || getStr(f, 1));
            return { ...base, type: 'envelope' as const, envelopeId, diamondCount: getInt(f, 3) };
        }

        // Proto: WebcastQuestionNewMessage { MessageType type=1, QuestionDetails questionDetails=2 }
        //   QuestionDetails { string questionText=2, User user=5 }
        case 'WebcastQuestionNewMessage': {
            let questionText = '', user: TikTokUser = { id: '0', nickname: '', uniqueId: '' };
            const detailBuf = getBytes(f, 2);
            if (detailBuf) {
                const df = decodeProto(detailBuf);
                questionText = getStr(df, 2);
                const userBuf = getBytes(df, 5);
                if (userBuf) user = parseUser(userBuf);
            }
            return { ...base, type: 'question' as const, user, questionText };
        }

        case 'WebcastRankUpdateMessage':
        case 'WebcastHourlyRankMessage': {
            const rankType = getStr(f, 1) || `rank_${getInt(f, 1)}`;
            const rankList: Array<{ user: TikTokUser; rank: number; score: number }> = [];
            const listBufs = getAllBytes(f, 2);
            for (const lb of listBufs) {
                try {
                    const rf = decodeProto(lb);
                    const userBuf = getBytes(rf, 1);
                    const rank = getInt(rf, 2);
                    const score = getInt(rf, 3);
                    const user = userBuf ? parseUser(userBuf) : { id: '0', nickname: '', uniqueId: '' };
                    rankList.push({ user, rank, score });
                } catch { }
            }
            return { ...base, type: 'rankUpdate' as const, rankType, rankList };
        }

        // Proto: WebcastControlMessage { int32 action=2 }
        case 'WebcastControlMessage':
            return { ...base, type: 'control' as const, action: getInt(f, 2) || getInt(f, 1) };

        case 'WebcastRoomMessage':
        case 'RoomMessage':
            return { ...base, type: 'room' as const, status: getInt(f, 2) };

        // Proto: WebcastLiveIntroMessage { uint64 id=2, string description=4, User user=5 }
        case 'WebcastLiveIntroMessage': {
            const roomId = String(getInt(f, 2));
            const title = getStr(f, 4) || getStr(f, 2);
            return { ...base, type: 'liveIntro' as const, roomId, title };
        }

        case 'WebcastLinkMicMethod':
        case 'WebcastLinkmicBattleTaskMessage': {
            const action = getStr(f, 1) || `action_${getInt(f, 1)}`;
            const users: TikTokUser[] = [];
            const userBufs = getAllBytes(f, 2);
            for (const ub of userBufs) {
                try { users.push(parseUser(ub)); } catch { }
            }
            return { ...base, type: 'linkMic' as const, action, users };
        }

        default:
            return { ...base, type: 'unknown' as const, method };
    }
}

export function parseWebcastResponse(payload: Buffer): LiveEvent[] {
    const events: LiveEvent[] = [];
    const respFields = decodeProto(payload);

    for (const mf of respFields.filter(f => f.fn === 1 && f.wt === 2)) {
        const msgBuf = mf.value as Buffer;
        const msgFields = decodeProto(msgBuf);
        const method = getStr(msgFields, 1);
        const innerPayload = getBytes(msgFields, 2);
        if (!method || !innerPayload) continue;

        try {
            events.push(parseWebcastMessage(method, innerPayload));
        } catch { }
    }

    return events;
}
